'use client';
import { CtfRichText } from '@/modules/common/components/CtfRichText';
import Image from '@/modules/common/components/Image';
import { AlgoliaProvider } from '@/modules/product/components/AlgoliaSearch/AlgoliaProvider';

import { AuthorFieldsFragment } from '@/gql/__generated/graphql.types';

import { BlogPostCard } from '../components/BlogPostCard';
import { BlogRecommendationProducts } from '../components/BlogRecommendationProducts';
import { PinterestIcon } from '../components/icons/PinterestIcon';
import { ThreadsIcon } from '../components/icons/ThreadsIcon';
import { XIcon } from '../components/icons/XIcon';
import { useBlogPostListByAuthorQuery } from '../gql/__generated/blogPost.generated';

type BlogPageTemplateProps = {
  author: AuthorFieldsFragment;
};

export const BlogAuthorPageTemplate = ({ author }: BlogPageTemplateProps) => {
  const { data: authorPosts } = useBlogPostListByAuthorQuery({ authorSlug: author.slug });

  const showFollowSection = author.twitter || author.threads || author.pinterest;

  return (
    <main className="app-container pt-10">
      <header className="flex max-w-[920px] gap-4 border-b border-gray-75 pb-8">
        {author.avatar && (
          <Image
            src={author.avatar?.url ?? ''}
            className="block h-32 w-32 rounded-xl object-contain"
            alt={author.avatar?.title ?? ''}
          />
        )}
        <div>
          <h1 className="mb-3 text-3xl font-bold lg:text-6xl">{author.name}</h1>
          <p className="text-2xl font-bold text-gray-400">{author.role}</p>
        </div>
      </header>

      <section className="max-w-[920px]">
        {author.description?.json && <CtfRichText {...author.description} />}
      </section>

      {showFollowSection && (
        <section className="flex gap-7 pb-16">
          <h2 className="font-bold">Follow on:</h2>
          {author.twitter && (
            <a href={author.twitter} aria-label="Twitter">
              <XIcon />
            </a>
          )}
          {author.threads && (
            <a href={author.threads} aria-label="Threads">
              <ThreadsIcon />
            </a>
          )}
          {author.pinterest && (
            <a href={author.pinterest} aria-label="Pinterest">
              <PinterestIcon />
            </a>
          )}
        </section>
      )}

      {!!authorPosts?.blogPostCollection?.items?.length && (
        <section className="mb-16">
          <h2 className="pb-9 pt-2 text-2xl font-bold leading-6 lg:pb-6 lg:text-[32px] lg:leading-8">
            Articles by {author.name}
          </h2>
          <div className="grid gap-4 lg:grid-cols-3">
            {authorPosts.blogPostCollection.items.map((blog) => (
              <BlogPostCard key={blog?.slug} blog={blog} />
            ))}
          </div>
        </section>
      )}

      <AlgoliaProvider>
        {author?.authorPicks?.length > 0 && author?.name ?
          <section className="pb-40">
            <BlogRecommendationProducts
              blogProducts={author.authorPicks}
              blogSlug={author.name.toLowerCase().split(' ').join('-')}
              title={`${author.name}’s Pick${author.authorPicks.length > 1 ? 's' : ''}`}
            />
          </section>
        : null}
      </AlgoliaProvider>
    </main>
  );
};
